import React from 'react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine } from 'recharts';

const ACTUAL_COLOR = '#6938EF';
const CAPACITY_COLOR = '#A4A7AE';
const FORECAST_COLOR = '#DC6803';

function parseShortDate(mdy) {
  if (!mdy) return 0;
  const [m, d, y] = mdy.split('/').map(Number);
  const fullYear = y < 100 ? 2000 + y : y;
  return new Date(fullYear, m - 1, d).getTime();
}

function mergeSeries(actual, forecast) {
  const byDate = new Map();
  (actual || []).forEach((row) => {
    byDate.set(row.date, { ...row });
  });
  (forecast || []).forEach((row) => {
    const existing = byDate.get(row.date) || { date: row.date };
    byDate.set(row.date, { ...existing, forecast: row.forecast });
  });
  return Array.from(byDate.values()).sort((a, b) => parseShortDate(a.date) - parseShortDate(b.date));
}

const CumulativeSalesChart = ({
  data, forecastCurve, capacity, todayDate,
  actualColor = ACTUAL_COLOR, forecastColor = FORECAST_COLOR, capacityLabel = true,
}) => {
  if (!Array.isArray(data) || data.length === 0) {
    return <div className="mi-chart-empty">No sales data yet.</div>;
  }

  const isDark = typeof document !== 'undefined'
    && document.documentElement.getAttribute('data-theme') === 'dark';
  const gridColor = isDark ? '#283040' : '#F5F5F5';
  const axisTextColor = isDark ? '#8B92A0' : '#717680';
  const todayColor = isDark ? '#CDD2DC' : '#0A0D12';

  const hasForecast = (forecastCurve?.length ?? 0) > 0;
  const merged = mergeSeries(data, forecastCurve);
  const todayMarker = todayDate || data[data.length - 1].date;
  const maxY = Math.max(
    capacity || 0,
    ...merged.map((d) => Math.max(d.number_of_tickets_sold || 0, d.forecast || 0)),
  );

  return (
    <ResponsiveContainer width="100%" height={360}>
      <AreaChart data={merged} margin={{ top: 20, right: capacityLabel ? 70 : 24, left: 10, bottom: 20 }}>
        <defs>
          <linearGradient id="cumulativeSalesActual" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={actualColor} stopOpacity={0.20} />
            <stop offset="100%" stopColor={actualColor} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke={gridColor} strokeDasharray="0" vertical={false} />
        <XAxis dataKey="date" tick={{ fontSize: 10, fill: axisTextColor }} tickLine={false} axisLine={false} />
        <YAxis domain={[0, maxY]} tick={{ fontSize: 10, fill: axisTextColor }} tickLine={false} axisLine={false} width={40} />
        <Tooltip
          contentStyle={{
            background: "var(--sl-bg-primary)",
            border: "1px solid var(--sl-border-secondary)",
            borderRadius: "8px",
            color: "var(--sl-text-primary)",
          }}
          labelStyle={{ color: "var(--sl-text-secondary)" }}
          itemStyle={{ color: "var(--sl-text-primary)" }}
          cursor={{ stroke: "var(--sl-border-secondary)" }}
          formatter={(value, name) => [
            value,
            name === "number_of_tickets_sold" ? "Tickets Sold" : name === "forecast" ? "Forecast" : name,
          ]}
        />
        {capacity > 0 && (
          <ReferenceLine
            y={capacity}
            stroke={CAPACITY_COLOR}
            strokeDasharray="4 4"
            label={capacityLabel ? { value: `Cap ${capacity}`, position: 'right', fill: axisTextColor, fontSize: 10 } : undefined}
          />
        )}
        <ReferenceLine
          x={todayMarker}
          stroke={todayColor}
          strokeDasharray="2 3"
          label={{ value: 'Today', position: 'top', fill: '#FFFFFF', fontSize: 10, fontWeight: 700, fontFamily: 'Open Sans' }}
        />
        <Area
          type="monotone"
          dataKey="number_of_tickets_sold"
          stroke={actualColor}
          strokeWidth={2.5}
          fill="url(#cumulativeSalesActual)"
          isAnimationActive={false}
          connectNulls
        />
        {hasForecast && (
          <Area
            type="monotone"
            dataKey="forecast"
            stroke={forecastColor}
            strokeWidth={2}
            strokeDasharray="6 4"
            fill="none"
            isAnimationActive={false}
            connectNulls
          />
        )}
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default CumulativeSalesChart;
